import RequestHandler from './RequestHandler';
import { urlHttp, apiKeyP80 } from '../configs/appConfig.json';

interface RetryEntry {
  retry:   (newToken: string) => Promise<any>;
  resolve: (value: any) => void;
  reject:  (reason: any) => void;
}

import Cookies from 'universal-cookie';

class ObexRequestHandlerP80 {
  // API_BASE_URL solo la define el webpack de desarrollo (configs/webpack/dev.js)
  // para apuntar al backend local; en producción no existe y manda appConfig.json.
  private static baseUrl = process.env.API_BASE_URL || urlHttp;
  private static cookies = new Cookies();

  private static _conflictPending = false;
  private static _retryQueue: RetryEntry[] = [];

  private static getAuthToken() {
    const user = ObexRequestHandlerP80.cookies.get('101Obex');
    if (!user) { return ''; }
    const { token } = user;
    return `Bearer ${token}`;
  }

  private static getHeaders(disableCache = false) {
    return {
      'Authorization': this.getAuthToken(),
      '101obexapikey': apiKeyP80,
      'disable-cache': disableCache,
    };
  }

  private static isSessionConflict(res: any): boolean {
    const msg: string = res?.response?.msg || (typeof res?.response === 'string' ? res.response : '') || res?.data?.result?.msg || res?.result?.msg || '';
    return /sesi[oó]n/i.test(msg) && /expirada/i.test(msg);
  }

  private static enqueueRetry(
    res:   any,
    retry: (newToken: string) => Promise<any>
  ): Promise<any> | null {
    if (!this.isSessionConflict(res)) return null;
    const self = this;
    return new Promise(function(resolve, reject) {
      self._retryQueue.push({ retry, resolve, reject });
      if (!self._conflictPending) {
        self._conflictPending = true;
        window.dispatchEvent(new CustomEvent('nm-session-conflict'));
      }
    });
  }

  // Called by DualSessionModal after successful re-login
  public static flushRetryQueue(newToken: string): void {
    const queue = this._retryQueue.splice(0);
    this._conflictPending = false;
    queue.forEach(function(entry) {
      entry.retry(newToken).then(entry.resolve).catch(entry.reject);
    });
  }

  private static getUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${cleanPath}`;
  }

  public static get(path: string, params?: object, disableCache = false): Promise<any> {
    const self    = this;
    const url     = this.getUrl(path);
    const headers = this.getHeaders(disableCache);
    return RequestHandler.get(url, params, headers)
      .then(function(res: any) {
        const queued = self.enqueueRetry(res, function(newToken: string) {
          const retryParams = Object.assign({}, params, { token: newToken });
          return RequestHandler.get(url, retryParams, headers);
        });
        return queued !== null ? queued : res;
      });
  }

  public static post(path: string, payload?: object): Promise<any> {
    const self    = this;
    const url     = this.getUrl(path);
    const headers = this.getHeaders();
    return RequestHandler.post(url, payload, headers)
      .then(function(res: any) {
        const queued = self.enqueueRetry(res, function(newToken: string) {
          const retryPayload = Object.assign({}, payload, { token: newToken });
          return RequestHandler.post(url, retryPayload, headers);
        });
        return queued !== null ? queued : res;
      });
  }

  public static put(path: string, payload?: object): Promise<any> {
    const self    = this;
    const url     = this.getUrl(path);
    const headers = this.getHeaders();
    return RequestHandler.put(url, payload, headers)
      .then(function(res: any) {
        const queued = self.enqueueRetry(res, function(newToken: string) {
          const retryPayload = Object.assign({}, payload, { token: newToken });
          return RequestHandler.put(url, retryPayload, headers);
        });
        return queued !== null ? queued : res;
      });
  }

  public static patch(path: string, payload?: object): Promise<any> {
    const self    = this;
    const url     = this.getUrl(path);
    const headers = this.getHeaders();
    return RequestHandler.patch(url, payload, headers)
      .then(function(res: any) {
        const queued = self.enqueueRetry(res, function(newToken: string) {
          const retryPayload = Object.assign({}, payload, { token: newToken });
          return RequestHandler.patch(url, retryPayload, headers);
        });
        return queued !== null ? queued : res;
      });
  }

  public static delete(path: string, params?: object): Promise<any> {
    const self    = this;
    const url     = this.getUrl(path);
    const headers = this.getHeaders();
    return RequestHandler.delete(url, params, headers)
      .then(function(res: any) {
        const queued = self.enqueueRetry(res, function(newToken: string) {
          const retryParams = Object.assign({}, params, { token: newToken });
          return RequestHandler.delete(url, retryParams, headers);
        });
        return queued !== null ? queued : res;
      });
  }
}

export default ObexRequestHandlerP80;
